/**
 * Portal Manta · agValidadorPreenchimento
 * Extraído de App.jsx v29.3 (via AST)
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { CONSOL_FIN } from '../../data/consol-fin.js';
import { pct, extKm } from '../../globals.js';
import { F } from '../../tokens/fonts.js';
import { M } from '../../tokens/colors.js';

function agValidadorPreenchimento(ctx, prev){
  const secoes = [];
  const benchmarks = [];
  const acoes = [];

  // ── 1. Identificação do estudo (KPIs qualitativos · 14 campos) ──
  const qual = ctx.qual || {};
  const camposQual = [
    {k:'nome', label:'Nome do estudo', critico:true},
    {k:'uf', label:'UF', critico:true},
    {k:'cidade', label:'Cidade/região'},
    {k:'tipoObra', label:'Tipo de obra'},
    {k:'concessionaria', label:'Concessionária'},
    {k:'dataEstudo', label:'Data do estudo'},
    {k:'nivelDetalhe', label:'Nível de detalhe'},
    {k:'ctxRural', label:'Contexto rural %'},
    {k:'ctxUrbano', label:'Contexto urbano %'},
    {k:'ctxSerra', label:'Contexto serra %'},
    {k:'chuvaMM', label:'Chuva mm/ano'},
    {k:'chuvaDias', label:'Dias chuva/ano'},
    {k:'chuvaRegime', label:'Regime de chuvas'},
    {k:'observacoes', label:'Observações'},
  ];
  if (Object.keys(qual).length > 0) {
    const faltas = camposQual.filter(c => qual[c.k] === undefined || qual[c.k] === '' || qual[c.k] === null);
    const criticosFaltando = faltas.filter(c => c.critico);
    secoes.push({
      label:'Identificação Qualitativa (Novo Estudo)',
      peso:10,
      total:camposQual.length,
      completo:camposQual.length - faltas.length,
      faltas:faltas.map(c=>c.label),
      criticosFaltando:criticosFaltando.length,
      pct: Math.round((camposQual.length - faltas.length) / camposQual.length * 100),
    });
    if (criticosFaltando.length > 0) {
      acoes.push({severidade:'alta', secao:'Identificação', msg:`Preencha campos críticos: ${criticosFaltando.map(c=>c.label).join(', ')}`});
    }
  }

  // ── 2. KPIs físicos do projeto (8 campos) ──
  const kpis = ctx.kpis || {};
  const camposKpi = [
    {k:'extKm', label:'Extensão (km)', critico:true},
    {k:'dupKm', label:'Duplicações (km)'},
    {k:'margKm', label:'Marginais (km)'},
    {k:'fxKm', label:'Faixas adicionais (km)'},
    {k:'ciclKm', label:'Ciclovias (km)'},
    {k:'oaeQt', label:'OAEs (un)'},
    {k:'passQt', label:'Passarelas (un)'},
    {k:'interQt', label:'Interseções (un)'},
  ];
  if (Object.keys(kpis).length > 0) {
    const preenchidos = camposKpi.filter(c => kpis[c.k] != null && kpis[c.k] !== '' && kpis[c.k] !== 0);
    const criticosFaltando = camposKpi.filter(c => c.critico && (!kpis[c.k] || kpis[c.k] === 0));
    secoes.push({
      label:'KPIs Físicos do Projeto',
      peso:10,
      total:camposKpi.length,
      completo:preenchidos.length,
      faltas:camposKpi.filter(c => !preenchidos.includes(c)).map(c=>c.label),
      criticosFaltando:criticosFaltando.length,
      pct: Math.round(preenchidos.length / camposKpi.length * 100),
    });
    if (criticosFaltando.length > 0) {
      acoes.push({severidade:'alta', secao:'KPIs Físicos', msg:`Extensão é obrigatória — sem ela o cosseno não calcula`});
    }
  }

  // ── 3. Q1.A · Obras de Ampliação ──
  const linhasObras = ctx.linhasObras || [];
  if (linhasObras.length > 0) {
    const touched = linhasObras.filter(l => l.touched);
    const semCusto = linhasObras.filter(l => !l.editalTotal && !l.totalRev);
    secoes.push({
      label:'Q1.A · Obras de Ampliação',
      peso:25,
      total:linhasObras.length,
      completo:linhasObras.length - semCusto.length,
      faltas:semCusto.map(l=>l.cod+' · '+l.desc.slice(0,40)),
      tocados:touched.length,
      pct: Math.round((linhasObras.length - semCusto.length) / Math.max(linhasObras.length,1) * 100),
    });
  }

  // ── 4. Q1.B · Indiretos ──
  const linhasIndiretos = ctx.linhasIndiretos || [];
  if (linhasIndiretos.length > 0) {
    const semBase = linhasIndiretos.filter(l => !l.base || l.base === 0);
    secoes.push({
      label:'Q1.B · Indiretos',
      peso:10,
      total:linhasIndiretos.length,
      completo:linhasIndiretos.length - semBase.length,
      faltas:semBase.map(l=>l.key+' · '+l.desc),
      pct: Math.round((linhasIndiretos.length - semBase.length) / Math.max(linhasIndiretos.length,1) * 100),
    });
    if (semBase.length === linhasIndiretos.length) {
      acoes.push({severidade:'alta', secao:'Q1.B Indiretos', msg:'Todos os 4 indiretos estão zerados · preencha meses × R$/mês'});
    } else if (semBase.length > 0) {
      acoes.push({severidade:'media', secao:'Q1.B Indiretos', msg:`${semBase.length} indireto(s) não preenchidos`});
    }
  }

  // ── 5. Q2 · BDI Detalhado (10 componentes) ──
  const bdiComp = ctx.bdiComp || {};
  const componentesBdi = ['AC','DF','R','CT','SG','L','PIS','COFINS','ISS','CPRB'];
  const bdiZerados = componentesBdi.filter(c => !bdiComp[c] || bdiComp[c] === 0);
  // CPRB pode legitimamente ser 0 (nem todo licitante usa) · não conta como falta
  const bdiZeradosUteis = bdiZerados.filter(c => c !== 'CPRB');
  secoes.push({
    label:'Q2 · BDI Detalhado',
    peso:20,
    total:componentesBdi.length,
    completo:componentesBdi.length - bdiZerados.length,
    faltas:bdiZeradosUteis,
    pct: Math.round((componentesBdi.length - bdiZerados.length) / componentesBdi.length * 100),
    bdiCalculado: ctx.bdiCalculado != null ? (ctx.bdiCalculado*100).toFixed(2)+'%' : 'não calculado',
  });
  if (bdiZeradosUteis.length >= 6) {
    acoes.push({severidade:'alta', secao:'Q2 BDI', msg:'Maioria dos componentes BDI zerados · use preset MEF ANTT ou DNIT 2022'});
  }

  // ── 6. Q3 · TOP-10 Serviços SICRO ──
  const top10Serv = ctx.top10ABCServ || [];
  const abcServOv = ctx.abcServOv || {};
  if (top10Serv.length > 0) {
    const revisados = top10Serv.filter(it => abcServOv[it.c] && (abcServOv[it.c].qtd != null || abcServOv[it.c].cu != null));
    secoes.push({
      label:'Q3 · TOP-10 Serviços SICRO',
      peso:15,
      total:top10Serv.length,
      completo:revisados.length,
      faltas:top10Serv.filter(it => !revisados.includes(it)).map(it => it.c+' · '+it.desc.slice(0,40)),
      pct: Math.round(revisados.length / top10Serv.length * 100),
    });
    if (revisados.length === 0) {
      acoes.push({severidade:'baixa', secao:'Q3 SICRO', msg:'Nenhum serviço SICRO foi revisado · valores do edital prevalecem (OK se intencional)'});
    }
  } else {
    secoes.push({
      label:'Q3 · TOP-10 Serviços SICRO',
      peso:15, total:0, completo:0, pct:0,
      faltas:['CONSOL_FIN['+ctx.sig+'] vazio · sem dados SICRO'],
    });
    acoes.push({severidade:'critica', secao:'Q3 SICRO', msg:'CONSOL_FIN['+ctx.sig+'] está vazio · contate admin do portal'});
  }

  // ── 7. Q4 · TOP-10 Insumos ──
  const abcInsLista = ctx.abcInsLista || [];
  secoes.push({
    label:'Q4 · TOP-10 Insumos',
    peso:5, // peso baixo · vazio é OK por design
    total:1, // simbólico
    completo:abcInsLista.length > 0 ? 1 : 0,
    pct:abcInsLista.length > 0 ? 100 : 0,
    faltas:abcInsLista.length === 0 ? ['Lista vazia · use ⚡ Pré-pop ou 🔬 Decompor Q3 (opcional)'] : [],
    n_insumos:abcInsLista.length,
  });

  // ── 8. Custo Direto e Total Geral coerentes ──
  const cd = ctx.custoDiretoManta || 0;
  const tg = ctx.totalGeralManta || 0;
  if (cd > 0 && tg > 0) {
    secoes.push({label:'Coerência Total', peso:5, total:1, completo:1, pct:100, valores:{custoDireto: cd, totalGeral: tg, fator: (tg/cd).toFixed(4)}});
  } else {
    secoes.push({label:'Coerência Total', peso:5, total:1, completo:0, pct:0, faltas:['Custo Direto ou Total Geral zerado']});
    if (cd === 0) acoes.push({severidade:'critica', secao:'Total', msg:'Custo Direto = R$ 0 · sem dados em obras nem indiretos'});
  }

  // ── 9. Benchmarks vs outros lotes (referência cruzada) ──
  const todosLotes = ['3','4','5'];
  const sig = ctx.sig;
  if (cd > 0 && ctx.ext > 0) {
    const cdkm = cd / ctx.ext;
    benchmarks.push({metrica:'Custo Direto Manta / km', lote:sig, valor:cdkm, fmt:'R$ '+(cdkm/1e6).toFixed(2)+' M/km'});
    // Comparar com benchmark típico rodoviário (R$ 6-15 mi/km dependendo do escopo)
    if (cdkm < 3e6) {
      acoes.push({severidade:'media', secao:'Benchmark', msg:'CD/km muito baixo (R$ '+(cdkm/1e6).toFixed(2)+' M/km) · típico é R$ 6-15 M/km · revise quantitativos'});
    } else if (cdkm > 25e6) {
      acoes.push({severidade:'media', secao:'Benchmark', msg:'CD/km muito alto (R$ '+(cdkm/1e6).toFixed(2)+' M/km) · típico é R$ 6-15 M/km · pode haver dupla contagem'});
    }
  }
  // BDI dentro de faixa razoável (15% a 35%)
  if (ctx.bdiCalculado != null) {
    const bdiPct = ctx.bdiCalculado * 100;
    benchmarks.push({metrica:'BDI calculado', lote:sig, valor:bdiPct, fmt:bdiPct.toFixed(2)+'%'});
    if (bdiPct < 15 || bdiPct > 35) {
      acoes.push({severidade:'media', secao:'Benchmark', msg:'BDI '+bdiPct.toFixed(2)+'% fora da faixa típica 15-35% · revise componentes'});
    }
  }

  // ── 10. Score consolidado (média ponderada) ──
  const pesoTotal = secoes.reduce((s,sec) => s + sec.peso, 0);
  const scoreSomado = secoes.reduce((s,sec) => s + sec.peso * sec.pct, 0);
  const scoreFinal = pesoTotal > 0 ? Math.round(scoreSomado / pesoTotal) : 0;

  // Status global
  let status = 'ok';
  if (acoes.some(a=>a.severidade==='critica')) status = 'critico';
  else if (acoes.some(a=>a.severidade==='alta')) status = 'atencao';
  else if (scoreFinal < 70) status = 'incompleto';

  return {
    sub:'Validador de Preenchimento',
    status,
    score:scoreFinal,
    msg: status==='ok' ? 'Tudo preenchido' : `${acoes.length} ação(ões) sugerida(s) · score ${scoreFinal}%`,
    secoes,
    benchmarks,
    lista_acionavel: acoes,
    timestamp: new Date().toISOString(),
  };
}

export { agValidadorPreenchimento };
export default agValidadorPreenchimento;
